import './styles.css'

import React, { useEffect, useRef, useState } from 'react'

import Alert from './Alert'
import { dictionary, version, wordPairs } from './data'

const calculateIndexByDate = () => {
  const date = new Date()

  // Shift the time *back* by 3 hours
  const shifted = new Date(date.getTime() - 3 * 60 * 60 * 1000)

  const localMilliseconds = new Date(
    shifted.getFullYear(),
    shifted.getMonth(),
    shifted.getDate()
  ).getTime()

  const daysSinceEpoch = localMilliseconds / (24 * 60 * 60 * 1000)
  return Math.floor(daysSinceEpoch)
}

const Game = () => {
  const initialTodayIndex = calculateIndexByDate() % wordPairs[0].length;
  const initialYesterdayIndex = (initialTodayIndex - 1) % wordPairs[0].length;

  const [todayIndex, setTodayIndex] = useState(initialTodayIndex);
  const [yesterdayIndex, setYesterdayIndex] = useState(initialYesterdayIndex);

  const [completionMessage, setCompletionMessage] = useState('')
  const [showCompletionMessage, setShowCompletionMessage] = useState(false)

  const [viewInstructions, setViewInstructions] = useState(true)
  const [expandedCircle, setExpandedCircle] = useState('score') // 'score' or 'best'
  const [highestLevel, setHighestLevel] = useState(1)
  const [modalWords, setModalWords] = useState([])
  const [loading, setLoading] = useState(true)
  const [achievedNewBest, setAchievedNewBest] = useState(false)
  const [wordPairIndex, setWordPairIndex] = useState(0)
  const [activeIndex, setActiveIndex] = useState(0)
  const [progressWord, setProgressWord] = useState('')
  const [progressScore, setProgressScore] = useState(0)
  const [progressCursor, setProgressCursor] = useState(-1)
  const [progressPath, setProgressPath] = useState([])
  const [currentPath, setCurrentPath] = useState([])
  const [currentWord, setCurrentWord] = useState('')
  const [currentLevel, setCurrentLevel] = useState(1)
  const [score, setScore] = useState(0)
  const [best, setBest] = useState(999)
  const [cursorPosition, setCursorPosition] = useState(-1)
  const [message, setMessage] = useState('')
  const [isGameStarted, setIsGameStarted] = useState(false)
  const [isGameFinished, setIsGameFinished] = useState(false)
  const [winEffect, setWinEffect] = useState(false)
  const [shakeEffect, setShakeEffect] = useState(false)
  const [danceEffect, setDanceEffect] = useState(false)
  const [superDanceEffect, setSuperDanceEffect] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)
  const [canVibrate, setCanVibrate] = useState(false)
  const [allSolved, setAllSolved] = useState(false)
  const gameContainerRef = useRef(null)
  const levelRef = useRef(null)

const [installPrompt, setInstallPrompt] = useState(null)
const [isIos, setIsIos] = useState(false)
const [isAndroid, setIsAndroid] = useState(false)
const [isSafari, setIsSafari] = useState(false)
const [isFirefox, setIsFirefox] = useState(false)

const handleInstallClick = () => {
  if (installPrompt) {
    installPrompt.prompt()
    installPrompt.userChoice.then(() => {
      setInstallPrompt(null)
    })
  }
}

useEffect(() => {
  const isIosDevice = /iphone|ipad|ipod/i.test(navigator.userAgent)
  const isAndroid = /android/i.test(navigator.userAgent)
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const isFirefox = /firefox/i.test(navigator.userAgent);

  const isStandalone = 'standalone' in window.navigator && window.navigator.standalone

  setIsIos(isIosDevice && !isStandalone)
  setIsAndroid(isAndroid)
  setIsSafari(isSafari)
  setIsFirefox(isFirefox)
}, [])

const updateCompletionMessage = () => {
  if (
    isSolved(1).solved &&
    isSolved(2).solved &&
    isSolved(3).solved &&
    isSolved(4).solved
  ) {
    const msg = areAllLevelsSolvedOptimally() ? 'perfect!\nall levels solved optimally!' : 'great!\nall levels solved!'
    setCompletionMessage(msg)
    setShowCompletionMessage(true)

    setTimeout(() => {
      setShowCompletionMessage(false)
    }, 7000)
  }
}

  const YesterdaysSolutions = () => {
    const [selectedLevel, setSelectedLevel] = useState(1) // Track selected tab
    const levels = [1, 2, 3, 4]

    const optimalSolutionRef = useRef(null)
    const playerSolutionRef = useRef(null)

    useEffect(() => {
      // Reset scroll position when selectedLevel changes
      if (optimalSolutionRef.current) {
        optimalSolutionRef.current.scrollLeft = 0
      }
      if (playerSolutionRef.current) {
        playerSolutionRef.current.scrollLeft = 0
      }
    }, [selectedLevel])

    // Fetch player's saved solution from localStorage
    const getPlayerSolution = (level) => {
      const storedData =
        JSON.parse(localStorage.getItem('subletters-game-data')) || {}
      const yesterdayIndexStr = yesterdayIndex.toString()

      if (
        storedData[yesterdayIndexStr] &&
        storedData[yesterdayIndexStr][level]
      ) {
        return (
          [
            wordPairs[level - 1][yesterdayIndex][0],
            ...storedData[yesterdayIndexStr][level]['path'],
            wordPairs[level - 1][yesterdayIndex].slice(-1)[0],
          ] || []
        )
      }
      return null // Return null if no solution exists
    }

    return (
      <div style={{ marginTop: '0.5em', textAlign: 'center', width: '100%' }}>
        {/* Tabs for each level */}
        <div
          style={{
            marginTop: '0.25em',
            display: 'flex',
            justifyContent: 'center',
          }}
        >
          {levels.map((level) => (
            <button
              key={level}
              onClick={() => setSelectedLevel(level)}
              style={{
                padding: '0.25em 0.25em',
                marginRight: '0.5em',
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor:
                  selectedLevel === level
                    ? 'var(--uchu-red)'
                    : 'var(--uchu-light-red)',
                color:
                  selectedLevel === level
                    ? 'var(--uchu-light-red)'
                    : 'var(--uchu-red)',
                border: 'none',
                fontWeight: 'bold',
                fontSize: '0.825em',
              }}
            >
              level {level}
            </button>
          ))}
        </div>

        {/* Display Provided Solution */}
        <div>
          <h5 style={{ marginTop: '0.75em', marginBottom: '0.25em' }}>
            an optimal solution
          </h5>
          <div
            style={{
              borderRadius: '9999px',
              backgroundColor: `var(--score-circle-color)`,
              cursor: 'pointer',
              maxWidth: '100%',
              height: '2em',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              flexShrink: 1,
            }}
          >
            <div
              ref={optimalSolutionRef}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                overflowX: 'auto',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
                height: '100%',
                padding: '0.25em 0.75em',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {wordPairs[selectedLevel - 1][yesterdayIndex].map((word, index) => (
                <span
                  key={index}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    color: 'var(--symbol-color)',
                    flexShrink: 0,
                    cursor: 'pointer',
                    userSelect: 'none',
                  }}
                >
                  <span style={{ margin: '0 3px' }}>{word}</span>
                  {index <
                    wordPairs[selectedLevel - 1][yesterdayIndex].length - 1 && (
                    <span
                      style={{
                        display: 'inline-flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        margin: '0 3px',
                        flexShrink: 0,
                        pointerEvents: 'none',
                      }}
                    >
                      <span style={{ fontSize: '0.6em', marginBottom: '-8px' }}>
                        {index + 1}
                      </span>
                      <span style={{ margin: '-5px 0', paddingBottom: '10px' }}>
                        →
                      </span>
                    </span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Display Player's Solution if available */}
        {getPlayerSolution(selectedLevel) && (
          <div style={{ marginTop: '0em' }}>
            <h5
              style={{
                color: 'var(--player-color)',
                marginTop: '0.5em',
                marginBottom: '0.25em',
              }}
            >
              your solution
            </h5>
            <div
              style={{
                borderRadius: '9999px',
                backgroundColor: `var(--score-circle-color)`,
                cursor: 'pointer',
                maxWidth: '100%',
                height: '2em',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                overflow: 'hidden',
                transition: 'all 0.3s ease',
                flexShrink: 1,
              }}
            >
              <div
                ref={playerSolutionRef}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  height: '100%',
                  padding: '0.25em 0.75em',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                }}
              >
                {getPlayerSolution(selectedLevel).map((word, index) => (
                  <span
                    key={index}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      color: 'var(--symbol-color)',
                      flexShrink: 0,
                      cursor: 'pointer',
                      userSelect: 'none',
                    }}
                  >
                    <span style={{ margin: '0 3px' }}>{word}</span>
                    {index < getPlayerSolution(selectedLevel).length - 1 && (
                      <span
                        style={{
                          display: 'inline-flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          margin: '0 3px',
                          flexShrink: 0,
                          pointerEvents: 'none',
                        }}
                      >
                        <span
                          style={{ fontSize: '0.6em', marginBottom: '-8px' }}
                        >
                          {index + 1}
                        </span>
                        <span
                          style={{ margin: '-5px 0', paddingBottom: '10px' }}
                        >
                          →
                        </span>
                      </span>
                    )}
                  </span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    )
  }

  const handleScoreCircleClick = () => {
    setExpandedCircle('score')
    setModalWords([
      wordPairs[currentLevel - 1][wordPairIndex][0],
      ...currentPath,
    ])
  }

  const handleBestCircleClick = () => {
    setExpandedCircle((prev) => {
        return 'best'
    })
    setModalWords([
      wordPairs[currentLevel - 1][wordPairIndex][0],
      ...progressPath,
      wordPairs[currentLevel - 1][wordPairIndex].slice(-1)[0],
    ])
  }

  useEffect(() => {
    handleScoreCircleClick() // Initially expand current path
  }, [currentPath])

  const toggleViewInstructions = () => {
    if (viewInstructions) {
      let dataToPersist =
        JSON.parse(localStorage.getItem('subletters-game-data')) || {}
      dataToPersist['played'] = true
      localStorage.setItem(
        'subletters-game-data',
        JSON.stringify(dataToPersist)
      )
    }
    setViewInstructions(!viewInstructions)
  }

  const showCurrentModal = () => {
    setModalWords([
      wordPairs[currentLevel - 1][wordPairIndex][0],
      ...currentPath,
    ])
  }

  const QwertyKeyboard = ({ handleKeyDown, handleShare, handleHelpClick }) => {
    const handleClick = (key) => {
      handleKeyDown({ key })
    }

    return (
      <div className="keyboard-container">
        {/* First row with 10 keys */}
        <div className="keyboard-row">
          {['q', 'w', 'e', 'r', 't', 'y', 'u', 'i', 'o', 'p'].map((key) => (
            <button
              key={key}
              onClick={() => handleClick(key)}
              className="keyboard-key"
            >
              {key}
            </button>
          ))}
        </div>
        {/* Second row with 9 keys */}
        <div className="keyboard-row">
          {['a', 's', 'd', 'f', 'g', 'h', 'j', 'k', 'l'].map((key) => (
            <button
              key={key}
              onClick={() => handleClick(key)}
              className="keyboard-key"
            >
              {key}
            </button>
          ))}
        </div>
        {/* Third row with 7 keys */}
        <div className="keyboard-row">
          <div
            style={{
              position: 'relative',
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 'auto',
              minWidth: '3em',
              height: 'auto',
              margin: '0.15rem',
            }}
          >
            <button
              onClick={
                isGameStarted && areAnyLevelsSolved() ? handleShare : undefined
              }
              className="keyboard-key share-key"
              style={{
                width: '100%',
                height: '100%',
                margin: '0',
              }}
            >
              <svg
                style={{ margin: 'auto', transform: 'translateY(0.0em)' }}
                width="1.25em"
                height="1.25em"
                viewBox="0 -5 570 550"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M160.389 93.8523C132.893 93.8523 116.902 155.728 116.003 175.974C115.036 197.73 114.437 215.243 134.716 227.475C157.736 241.36 187.531 246.288 212.662 234.589C252.076 216.241 281.645 164.078 260.451 121.69C246.337 93.4614 203.564 82.717 174.308 82.717"
                  stroke={
                    areAnyLevelsSolved()
                      ? 'var(--share-color)'
                      : 'var(--share-color-inactive)'
                  }
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M341.567 378.256C333.053 345.395 328.846 305.537 314.574 274.694C303.737 251.274 274.424 247.601 251.963 243.308C228.203 238.766 204.866 243.67 181.002 244.439C159.861 245.121 138.755 245.745 117.626 246.482C100.688 247.072 81.3447 250.131 65.0278 254.358C48.3085 258.69 38.2646 253.82 31.4407 274.564C23.1165 299.868 20.5005 333.997 26.2439 359.945C30.4834 379.1 33.4047 398.681 38.4059 417.983C44.9461 443.225 59.9432 433.863 82.6113 433.21C88.3957 433.043 102.224 430.973 106.865 426.926C115.252 419.611 108.669 407.055 109.469 398.133C110.22 389.768 110.259 381.72 110.46 373.592C110.554 369.802 115.353 371.313 116.165 374.031C119.083 383.787 121.292 393.816 123.846 403.674C131.824 434.465 139.802 465.256 147.78 496.046C154.717 522.82 207.754 508.483 226.529 506.476C235.402 505.528 256.213 505.393 263.447 498.509C274.676 487.823 263.583 455.689 262.113 443.416C260.043 426.129 252.398 405.521 253.577 388.273C254.01 381.938 248.475 367.401 251.701 361.919C259.499 348.667 281.886 388.563 286.085 391.034C292.399 394.749 307.791 386.461 314.968 386.586C321.468 386.699 340.532 385.36 338.174 376.259"
                  stroke={
                    areAnyLevelsSolved()
                      ? 'var(--share-color)'
                      : 'var(--share-color-inactive)'
                  }
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M425.864 13C454.684 13 468.751 35.5476 471.834 63.5556C474.345 86.3591 447.796 104 427.868 104C381.837 104 374.435 23.955 417.85 13"
                  stroke={
                    areAnyLevelsSolved()
                      ? 'var(--share-color)'
                      : 'var(--share-color-inactive)'
                  }
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M328.898 204.163C335.847 172.002 338.87 127.7 372.707 111.429C386.444 104.823 407.647 101.732 422.764 104.561C442.135 108.187 461.946 107.882 481.313 111.012C493.005 112.902 507.487 111.337 517.846 118.906C528.679 126.822 529.171 142.589 530.766 154.77C531.966 163.933 529.332 173.689 530.263 183.139C531.286 193.523 529.057 205.725 526.851 215.933C525.287 223.174 523.722 230.415 522.157 237.655C520.848 243.713 506.181 240.404 501.02 239.289C492.011 237.342 462.397 236.811 465.316 223.306C466.951 215.736 468.587 208.166 470.223 200.596C471.018 196.916 470.091 182.077 467.577 193.709C461.247 223.005 461.046 252.633 454.702 281.988C450.01 303.705 401.074 289.61 388.016 285.033C379.118 281.913 374.095 280.816 373.254 271.508C371.902 256.538 379.608 238.653 382.485 224.01C384.491 213.795 382.947 202.741 385.125 192.657C387.651 180.972 380.701 184.436 378.49 194.669C376.601 203.409 374.713 212.149 372.824 220.889C371.463 227.186 363.778 226.169 357.946 224.909C344.501 222.003 339.738 216.342 330.019 208.539"
                  stroke={
                    areAnyLevelsSolved()
                      ? 'var(--share-color)'
                      : 'var(--share-color-inactive)'
                  }
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M388.637 468.903C389.248 444.46 395.535 418.629 399.979 394.571C401.427 386.735 406.337 374.622 410.624 367.88C413.095 363.995 425.825 365.816 429.1 366.461C452.668 371.103 478.889 377.731 501.397 386.183C513.961 390.901 530.791 395.705 540.43 405.191"
                  stroke={
                    areAnyLevelsSolved()
                      ? 'var(--share-color)'
                      : 'var(--share-color-inactive)'
                  }
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M420.765 369.152C440.764 399.298 461.266 429.165 480.782 459.623C490.962 475.51 496.028 492.635 502.586 510.116C505.084 516.774 509.736 524.96 508.414 531.491"
                  stroke={
                    areAnyLevelsSolved()
                      ? 'var(--share-color)'
                      : 'var(--share-color-inactive)'
                  }
                  strokeWidth="32"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          {['z', 'x', 'c', 'v', 'b', 'n', 'm'].map((key) => (
            <button
              key={key}
              onClick={() => handleClick(key)}
              className="keyboard-key"
            >
              {key}
            </button>
          ))}
          <div
            style={{
              position: 'relative',
              display: 'inline-flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: 'auto',
              minWidth: '3em',
              height: 'auto',
              margin: '0.15rem',
            }}
          >
            <button
              onClick={handleHelpClick}
              className="keyboard-key"
              style={{
                backgroundColor: viewInstructions
                  ? 'var(--uchu-red)'
                  : 'transparent',
                color: viewInstructions
                  ? 'var(--uchu-yang)'
                  : 'var(--uchu-red)',
                margin: '0',
                height: '100%',
                width: '100%',
              }}
            >
              ??
            </button>
            {viewInstructions && (
              <div
                style={{
                  position: 'absolute',
                  top: '120%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 0,
                  height: 0,
                  borderLeft: '10px solid transparent',
                  borderRight: '10px solid transparent',
                  borderBottom: '10px solid var(--uchu-red)',
                }}
              />
            )}
          </div>
        </div>
      </div>
    )
  }

  const handleWordClick = (index, progress) => {
    if (index === 0) {
      handleRetryClick()
      return
    }

    if (isGameFinished) {
      initGame(todayIndex)
    }

    const newPath = (progress ? progressPath : currentPath).slice(0, index)
    const selectedWord = newPath[newPath.length - 1] || ''

    setCurrentPath(newPath)
    setProgressWord(selectedWord)
    setCurrentWord(selectedWord)

    const newScore = newPath.length
    setScore(newScore)
    setProgressScore(newScore)
  }

  const ScoreCircles = ({
    score,
    best,
    words,
    currentLevel,
    handleWordClick,
    expandedCircle,
    handleScoreCircleClick,
    handleBestCircleClick,
  }) => {
    const scrollRef = useRef(null)

    useEffect(() => {
      if (scrollRef.current) {
        scrollRef.current.scrollLeft = scrollRef.current.scrollWidth
      }
    }, [words, expandedCircle])

    const isZero = score === 0 || achievedNewBest

    return (
      <div
        style={{
          textAlign: 'center',
          margin: '1em 0 0.5em 0',
          fontSize: '1.25em',
          fontWeight: 'bold',
          color: `var(--level-${currentLevel}-text-color)`,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          width: '100%',
        }}
      >
        <div
          onClick={handleScoreCircleClick}
          style={{
            borderRadius: '9999px',
            backgroundColor: `var(--score-circle-color)`,
            cursor: isZero ? 'auto' : 'pointer',
            minWidth: isZero ? '0' : '2em',
            width: isZero ? '0' : 'auto',
            opacity: isZero ? '0' : 'inherit',
            height: '2em',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            flexShrink: 1,
          }}
        >
          {expandedCircle === 'score' ? (
            <div
              ref={scrollRef}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                overflowX: 'auto',
                overflowY: 'hidden',
                whiteSpace: 'nowrap',
                height: '100%',
                padding: score > 0 ? '0.25em 0.75em' : '0',
                scrollbarWidth: 'none',
                msOverflowStyle: 'none',
                WebkitOverflowScrolling: 'touch',
              }}
            >
              {isZero
                ? 0
                : words.map((word, index) => (
                    <span
                      key={index}
                      onClick={() => handleWordClick(index, false)}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        flexShrink: 0,
                        cursor: 'pointer',
                        userSelect: 'none',
                      }}
                    >
                      <span style={{ margin: '0 3px' }}>{word}</span>
                      {index < words.length - 1 && (
                        <span
                          style={{
                            display: 'inline-flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            margin: '0 3px',
                            flexShrink: 0,
                            pointerEvents: 'none',
                          }}
                        >
                          <span
                            style={{ fontSize: '0.6em', marginBottom: '-8px' }}
                          >
                            {index + 1}
                          </span>
                          <span
                            style={{ margin: '-5px 0', paddingBottom: '10px' }}
                          >
                            →
                          </span>
                        </span>
                      )}
                    </span>
                  ))}
            </div>
          ) : (
            score
          )}
        </div>

        {best < 999 && (
          <div
            onClick={handleBestCircleClick}
            style={{
              borderRadius: '9999px',
              backgroundColor: 'var(--best-circle-color)',
              cursor: 'pointer',
              minWidth: '2em',
              height: '2em',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              marginLeft: isZero ? '0' : '0.5em',
              flexShrink: 1,
            }}
          >
            {expandedCircle === 'best' ? (
              <div
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  overflowX: 'auto',
                  overflowY: 'hidden',
                  whiteSpace: 'nowrap',
                  height: '100%',
                  padding: '0.25em 0.75em',
                  scrollbarWidth: 'none',
                  msOverflowStyle: 'none',
                  WebkitOverflowScrolling: 'touch',
                }}
                onClick={(e) => {
                  // prevent scroll reset
                  e.stopPropagation()
                }}
              >
                {words.map((word, index) => (
                  <span
                    key={index}
                    onClick={() => {
                      if (index === words.length - 1) {
                          return
                      }

                      handleWordClick(index, true)
                    }}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      flexShrink: 0,
                      userSelect: 'none',
                    }}
                  >
                    <span style={{ margin: '0 3px' }}>{word}</span>
                    {index < words.length - 1 && (
                      <span
                        style={{
                          display: 'inline-flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          margin: '0 3px',
                          flexShrink: 0,
                          pointerEvents: 'none',
                        }}
                      >
                        <span
                          style={{ fontSize: '0.6em', marginBottom: '-8px' }}
                        >
                          {index + 1}
                        </span>
                        <span
                          style={{ margin: '-5px 0', paddingBottom: '10px' }}
                        >
                          →
                        </span>
                      </span>
                    )}
                  </span>
                ))}
              </div>
            ) : (
              best
            )}
          </div>
        )}
      </div>
    )
  }

  useEffect(() => {
    const storedData = localStorage.getItem('subletters-game-data')
    if (!storedData) {
      let dataToPersist = {}
      dataToPersist['played'] = false
      dataToPersist['version'] = version
      localStorage.setItem(
        'subletters-game-data',
        JSON.stringify(dataToPersist)
      )

      initGame(todayIndex)
      return
    }

    const parsedData = JSON.parse(storedData)
    const parsedIndex = parsedData[[todayIndex.toString()]]
    let loadedHighestLevel = 1
    if (parsedIndex && parsedIndex['challenge']) {
      if (parsedIndex['challenge'][5]) {
        loadedHighestLevel = 5
      } else if (parsedIndex['challenge'][4]) {
        loadedHighestLevel = 4
      } else if (parsedIndex['challenge'][3]) {
        loadedHighestLevel = 3
      } else if (parsedIndex['challenge'][2]) {
        loadedHighestLevel = 2
      } else {
        loadedHighestLevel = 1
      }
    }

    setHighestLevel(loadedHighestLevel)

    const played = parsedData['played']
    const parsedVersion = parsedData['version']
    if (!parsedVersion || (parsedVersion && parsedVersion !== version)) {
      let dataToPersist = {}
      dataToPersist['played'] = played
      dataToPersist['version'] = version
      localStorage.setItem(
        'subletters-game-data',
        JSON.stringify(dataToPersist)
      )
      initGame(todayIndex, true, 1)
    }

    if (played) {
      setViewInstructions(false)
    }

    initGame(todayIndex, true, loadedHighestLevel)
  }, [])

useEffect(() => {
  const interval = setInterval(() => {
    const newIndex = calculateIndexByDate() % wordPairs[0].length
    if (newIndex !== todayIndex) {
        setTodayIndex(newIndex)
        initGame(newIndex, true, 1)
    }
  }, 1000)

  return () => clearInterval(interval)
}, [todayIndex])

useEffect(() => {
  if (currentLevel >= 4) return
  if (!isSolved(currentLevel).solved || hasUnlockedLevel(currentLevel + 1)) {
    return
  }

  if (!winEffect && !danceEffect) {
      return
  }

  const timeout = setTimeout(() => {
    handleUnlockLevel(currentLevel + 1)
  }, winEffect ? 4000 : 2500)

  return () => clearTimeout(timeout)
}, [best, winEffect, danceEffect])

  useEffect(() => {
    showCurrentModal()
  }, [currentPath])

  useEffect(() => {
    let newTodayIndex = calculateIndexByDate() % wordPairs[currentLevel - 1].length
    let newYesterdayIndex = (todayIndex - 1) % wordPairs[currentLevel - 1].length
    if (todayIndex !== newTodayIndex) {
        setTodayIndex(newTodayIndex)
        setYesterdayIndex(newYesterdayIndex)
    }
  }, [currentLevel])

  useEffect(() => {
    const endWord = wordPairs[currentLevel - 1][wordPairIndex].slice(-1)[0]
    if (currentWord !== endWord) {
      return
    }

    if (!score) {
      return
    }

    setIsGameFinished(true)

    const optimal = wordPairs[currentLevel - 1][wordPairIndex].length - 1
    if (score === optimal) {
      const keyboardKeys = document.querySelectorAll('.keyboard-key')
      keyboardKeys.forEach((key) => {
        key.style.backgroundColor = `var(--target-light-color)`
      })
      setWinEffect(true)
      setSuperDanceEffect(true)
    } else {
      setDanceEffect(true)
    }

    if (isGameStarted) {
      setProgressScore(0)
      setProgressCursor(-1)
    }
  }, [currentLevel, currentWord, wordPairIndex])

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (best !== 999 && score !== 0) {
      if (hasUnlockedLevel(currentLevel + 1) && !isSolved(currentLevel).solved_optimally) {
        handleBestCircleClick()
      }
      if (!allSolved || areAllLevelsSolvedOptimally()) {
        updateCompletionMessage()
      }
    }
  }, [best])

  useEffect(() => {
    if (loading) return
    initGame(wordPairIndex, true, currentLevel)
  }, [currentLevel])

  useEffect(() => {
    if (shakeEffect && canVibrate) {
      navigator.vibrate([50, 175, 50, 175, 50])
    }
  }, [shakeEffect, canVibrate])

  useEffect(() => {
    if (loading) return
    initGame(todayIndex, true, 1)
  }, [todayIndex])

  const initGame = (index, reset = false, level = currentLevel) => {
    if (gameContainerRef.current) {
      gameContainerRef.current.focus()
    }

    if (progressScore === 0) {
      setCurrentPath([])
    }

    const start = wordPairs[level - 1][index][0]
    setWordPairIndex(index)
    setAllSolved(isSolved(1).solved && isSolved(2).solved && isSolved(3).solved && isSolved(4).solved)
    setHighestLevel(reset ? 1 : highestLevel)
    setCurrentWord(reset ? start : progressWord ? progressWord : start)
    setCurrentLevel(level)
    setScore(reset ? 0 : progressScore)
    setCursorPosition(reset ? -1 : progressCursor)
    setMessage('')
    setIsGameStarted(true)
    setIsGameFinished(false)
    setWinEffect(false)
    setDanceEffect(false)
    setSuperDanceEffect(false)
    setShakeEffect(false)
    setProgressPath([])
    setAchievedNewBest(false)
    setExpandedCircle('score')

    document.body.style.backgroundColor = 'var(--uchu-yang)'
    const keyboardKeys = document.querySelectorAll('.keyboard-key')
    keyboardKeys.forEach((key) => {
      key.style.backgroundColor = `var(--uchu-light-gray-transparent)`
    })

    const storedData = localStorage.getItem('subletters-game-data')
    let parsedBest = 999
    if (storedData) {
      const parsedData = JSON.parse(storedData)
      const parsedIndex = parsedData[[index.toString()]]
      if (!parsedIndex) {
        setBest(999)
      } else {
        if (!parsedIndex[level] || typeof parsedIndex[level] !== 'object') {
          parsedIndex[level] = {}
        }
        parsedBest = parsedIndex ? parsedIndex[level]['score'] : parsedBest
        let parsedPath = parsedIndex ? parsedIndex[level]['path'] : []
        if (parsedBest >= wordPairs[level - 1][index].length - 2) {
          setBest(parsedBest)
          setProgressPath(parsedPath)
        } else {
          setBest(999)
        }
      }
    } else {
      setBest(parsedBest)
    }

    setLoading(false)
  }

  const isSolved = (level) => {
    const wordIndex = isGameStarted ? wordPairIndex : yesterdayIndex
    const optimal = wordPairs[level - 1][wordIndex].length - 1
    const storedData = localStorage.getItem('subletters-game-data')
    if (storedData) {
      const parsedData = JSON.parse(storedData)
      const parsedIndex = parsedData[[wordIndex.toString()]]
      if (parsedIndex) {
        if (!parsedIndex[level] || typeof parsedIndex[level] !== 'object') {
          parsedIndex[level] = {}
        }
        const parsedBest = parsedIndex[level]['score']
        if (parsedBest) {
          return { solved: true, solved_optimally: parsedBest === optimal }
        }
      }
    }
    return { solved: false, solved_optimally: false }
  }

  const areAnyLevelsSolved = () => {
    let solved = false
    for (let i = 1; i <= highestLevel; i++) {
      solved = solved || isSolved(i).solved
    }
    return solved
  }

  const areAllLevelsSolvedOptimally = () => {
    return (
      isSolved(1).solved_optimally &&
      isSolved(2).solved_optimally &&
      isSolved(3).solved_optimally &&
      isSolved(4).solved_optimally
    )
  }

  const hasUnlockedLevel = (level) => {
    const storedData = localStorage.getItem('subletters-game-data')
    if (!storedData) {
      return false
    }

    const parsedData = JSON.parse(storedData)
    const parsedIndex = parsedData[[todayIndex.toString()]]
    if (!parsedIndex) {
      return false
    }

    if (!parsedIndex['challenge']) {
      return false
    }

    return parsedIndex['challenge'][level]
  }

  const handleHelpClick = () => {
    toggleViewInstructions()
  }

  const changeLevel = (i) => {
    if (currentLevel === i) return
    setCurrentPath([])
    setCurrentLevel(i)
    if (isGameStarted) {
      setProgressWord('')
      setProgressCursor(-1)
      setProgressScore(0)
    }
  }

  const handleRetryClick = () => {
    setCurrentPath([])
    setProgressWord('')
    setProgressCursor(-1)
    setProgressScore(0)
    initGame(wordPairIndex, true)
  }

  const handleUnlockLevel = (level) => {
    let dataToPersist =
      JSON.parse(localStorage.getItem('subletters-game-data')) || {}
    if (!dataToPersist[wordPairIndex.toString()]['challenge']) {
      dataToPersist[wordPairIndex.toString()]['challenge'] = {}
    }
    dataToPersist[wordPairIndex.toString()]['challenge'][level] = true
    localStorage.setItem('subletters-game-data', JSON.stringify(dataToPersist))
    setHighestLevel(level)
    changeLevel(level)
  }

  const generateShareText = () => {
    let text = '𝗌𝗎𝖻𝗹𝗲𝘁𝘁𝗲𝗿𝘀\n'
    const storedData = localStorage.getItem('subletters-game-data')
    if (storedData) {
      const parsedData = JSON.parse(storedData)
      const parsedIndex = parsedData[[wordPairIndex.toString()]]
      if (parsedIndex) {
        for (let level = 1; level <= 4; level++) {
          if (parsedIndex[level]) {
            const parsedBest = parsedIndex[level]['score']
            if (parsedBest) {
              text += `${wordPairs[level - 1][todayIndex].at(0)} → ${wordPairs[level - 1][todayIndex].at(-1)} : ${parsedBest}\n`
            }
          }
        }
      }
    }
    text = text.trim()
    return text
  }

  const handleShare = async () => {
    const isFirefox =
      typeof navigator.userAgent !== 'undefined' &&
      /firefox/i.test(navigator.userAgent)
    let text = generateShareText()
    if (navigator.share) {
      try {
        if (isFirefox) {
          console.log('Web Share API not fully supported in this browser.')
          try {
            await navigator.clipboard.writeText(`${text}`)
            setMessage('score copied')
          } catch (error) {
            console.error('Copy to clipboard failed:', error)
          }
          return
        }
        await navigator.share({
          text: text,
        })
      } catch (error) {
        console.error('Sharing failed:', error)
      }
    } else {
      console.log('Web Share API not supported in this browser.')
      try {
        await navigator.clipboard.writeText(`${text}`)
        setMessage('score copied')
      } catch (error) {
        console.error('Copy to clipboard failed:', error)
      }
    }
  }

  const handleCharacterClick = (index) => {
    setCursorPosition(index)
    setProgressCursor(index)
  }

  const handleKeyDown = (e) => {
    if (e.hasOwnProperty('preventDefault')) {
      e.preventDefault()
    }

    if (!isGameStarted || isGameFinished || cursorPosition < 0) {
      return
    }

    if ('vibrate' in navigator) {
      navigator.vibrate(1)
      setCanVibrate(true)
    }

    if (e.key === 'ArrowLeft') {
      let newCursorPosition = Math.max(0, cursorPosition - 1)
      setCursorPosition(newCursorPosition)
      setProgressCursor(newCursorPosition)
    } else if (e.key === 'ArrowRight') {
      let newCursorPosition = Math.min(
        currentWord.length - 1,
        cursorPosition + 1
      )
      setCursorPosition(newCursorPosition)
      setProgressCursor(newCursorPosition)
    } else if (e.key.length === 1 && e.key.match(/[a-z]/i)) {
      const newWord =
        currentWord.slice(0, cursorPosition) +
        e.key.toLowerCase() +
        currentWord.slice(cursorPosition + 1)
      if (newWord === currentWord) return
      if (dictionary.has(newWord)) {
        //setMessage('')
        setCurrentWord(newWord)
        setProgressWord(newWord)
        setScore((prevScore) => {
          const newScore = prevScore + 1
          setProgressScore(newScore)
          const end = wordPairs[currentLevel - 1][wordPairIndex].slice(-1)[0]
          setCurrentPath((prevList) => {
            const updatedList = [...prevList, newWord]
            return updatedList
          })
          if (newWord === end) {
            setIsGameFinished(true)
            setProgressWord('')
            if (best > 0 && newScore < best) {
              setProgressPath([...currentPath])
              setAchievedNewBest(true)
              setBest(newScore)
              let dataToPersist =
                JSON.parse(localStorage.getItem('subletters-game-data')) || {}
              const wordPairKey = wordPairIndex.toString()
              if (!dataToPersist[wordPairKey]) {
                dataToPersist[wordPairKey] = {}
              }
              if (
                !dataToPersist[wordPairKey][currentLevel] ||
                typeof dataToPersist[wordPairKey][currentLevel] !== 'object'
              ) {
                dataToPersist[wordPairKey][currentLevel] = {}
              }
              dataToPersist[wordPairKey][currentLevel]['score'] = newScore
              dataToPersist[wordPairKey][currentLevel]['path'] = currentPath
              if (!dataToPersist[wordPairKey]['challenge']) {
                  dataToPersist[wordPairKey]['challenge'] = {}
              }
              if (currentLevel < 4) {
                  if (!dataToPersist[wordPairKey]['challenge'][currentLevel + 1]) {
                      dataToPersist[wordPairKey]['challenge'][currentLevel + 1] = false
                  }
              }
              localStorage.setItem(
                'subletters-game-data',
                JSON.stringify(dataToPersist)
              )
            }
          }
          return newScore
        })
      } else {
        setShakeEffect(true)
        setTimeout(() => {
          setShakeEffect(false)
        }, 500)
      }
    }
  }

  function Accordion() {
    const items = [
      {
        title: 'rules',
        content: (
          <>
            <h4 style={{ marginTop: '0.5em', marginBottom: '0' }}>goal</h4>
            <p>Transform the start word into the target word.</p>

            <h4 style={{ marginTop: '0.5em', marginBottom: '0' }}>play</h4>
            <p>
              <strong>
                <em>Tap</em>
              </strong>{' '}
              a letter you want to change.{' '}
              <strong>
                <em>Tap</em>
              </strong>{' '}
              its replacement letter on the keyboard to form a new word. The new
              word must be in the valid word list.
            </p>
            <h4 style={{ marginTop: '0.5em', marginBottom: '0' }}>undo or restart</h4>
            <p>
              <span className="current-circle"></span> shows your path in
              progress. <span className="best-circle"></span> shows your
              shortest completed path.{' '}
              <strong>
                <em>Tap</em>
              </strong>{' '}
              any word in either path to undo back to that word.{' '}
              <strong>
                <em>Tap</em>
              </strong> the first word in your path to restart completely.
            </p>
            <h4 style={{ marginTop: '0.5em', marginBottom: '0' }}>levels</h4>
            <p>
              There are 4 levels of increasing difficulty. Find a path of any
              length to the target word to unlock the next level. For a greater
              challenge, try solving each level optimally in the minimum possible number
              of steps: 3, 4, 5, and 7, respectively. An optimal solution turns
              the level gold.
            </p>
            <h4 style={{ marginTop: '0.5em', marginBottom: '0' }}>sharing</h4>
            <p>
              <strong>
                <em>Tap</em>
              </strong>
              <svg
                style={{ margin: '0em 0.5em', transform: 'translateY(0.1em)' }}
                width="1em"
                height="1em"
                viewBox="0 -5 570 550"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M160.389 93.8523C132.893 93.8523 116.902 155.728 116.003 175.974C115.036 197.73 114.437 215.243 134.716 227.475C157.736 241.36 187.531 246.288 212.662 234.589C252.076 216.241 281.645 164.078 260.451 121.69C246.337 93.4614 203.564 82.717 174.308 82.717"
                  stroke="var(--share-color)"
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M341.567 378.256C333.053 345.395 328.846 305.537 314.574 274.694C303.737 251.274 274.424 247.601 251.963 243.308C228.203 238.766 204.866 243.67 181.002 244.439C159.861 245.121 138.755 245.745 117.626 246.482C100.688 247.072 81.3447 250.131 65.0278 254.358C48.3085 258.69 38.2646 253.82 31.4407 274.564C23.1165 299.868 20.5005 333.997 26.2439 359.945C30.4834 379.1 33.4047 398.681 38.4059 417.983C44.9461 443.225 59.9432 433.863 82.6113 433.21C88.3957 433.043 102.224 430.973 106.865 426.926C115.252 419.611 108.669 407.055 109.469 398.133C110.22 389.768 110.259 381.72 110.46 373.592C110.554 369.802 115.353 371.313 116.165 374.031C119.083 383.787 121.292 393.816 123.846 403.674C131.824 434.465 139.802 465.256 147.78 496.046C154.717 522.82 207.754 508.483 226.529 506.476C235.402 505.528 256.213 505.393 263.447 498.509C274.676 487.823 263.583 455.689 262.113 443.416C260.043 426.129 252.398 405.521 253.577 388.273C254.01 381.938 248.475 367.401 251.701 361.919C259.499 348.667 281.886 388.563 286.085 391.034C292.399 394.749 307.791 386.461 314.968 386.586C321.468 386.699 340.532 385.36 338.174 376.259"
                  stroke="var(--share-color)"
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M425.864 13C454.684 13 468.751 35.5476 471.834 63.5556C474.345 86.3591 447.796 104 427.868 104C381.837 104 374.435 23.955 417.85 13"
                  stroke="var(--share-color)"
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M328.898 204.163C335.847 172.002 338.87 127.7 372.707 111.429C386.444 104.823 407.647 101.732 422.764 104.561C442.135 108.187 461.946 107.882 481.313 111.012C493.005 112.902 507.487 111.337 517.846 118.906C528.679 126.822 529.171 142.589 530.766 154.77C531.966 163.933 529.332 173.689 530.263 183.139C531.286 193.523 529.057 205.725 526.851 215.933C525.287 223.174 523.722 230.415 522.157 237.655C520.848 243.713 506.181 240.404 501.02 239.289C492.011 237.342 462.397 236.811 465.316 223.306C466.951 215.736 468.587 208.166 470.223 200.596C471.018 196.916 470.091 182.077 467.577 193.709C461.247 223.005 461.046 252.633 454.702 281.988C450.01 303.705 401.074 289.61 388.016 285.033C379.118 281.913 374.095 280.816 373.254 271.508C371.902 256.538 379.608 238.653 382.485 224.01C384.491 213.795 382.947 202.741 385.125 192.657C387.651 180.972 380.701 184.436 378.49 194.669C376.601 203.409 374.713 212.149 372.824 220.889C371.463 227.186 363.778 226.169 357.946 224.909C344.501 222.003 339.738 216.342 330.019 208.539"
                  stroke="var(--share-color)"
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M388.637 468.903C389.248 444.46 395.535 418.629 399.979 394.571C401.427 386.735 406.337 374.622 410.624 367.88C413.095 363.995 425.825 365.816 429.1 366.461C452.668 371.103 478.889 377.731 501.397 386.183C513.961 390.901 530.791 395.705 540.43 405.191"
                  stroke="var(--share-color)"
                  strokeWidth="32"
                  strokeLinecap="round"
                />
                <path
                  d="M420.765 369.152C440.764 399.298 461.266 429.165 480.782 459.623C490.962 475.51 496.028 492.635 502.586 510.116C505.084 516.774 509.736 524.96 508.414 531.491"
                  stroke="var(--share-color)"
                  strokeWidth="32"
                  strokeLinecap="round"
                />
              </svg>
              to share your path lengths with others. <em>
                <strong>
                  <u>Paths will not be saved when playing in private or incognito
                    mode
                  </u>
                </strong>
              </em>
              .
            </p>
{(isIos && !isAndroid) && (
  <>
    <h4 style={{ marginTop: '0.5em', marginBottom: '0' }}>install</h4>
      <p>
        To install <strong>subletters</strong> as an app,{' '}
        <strong><em>tap</em></strong> the share icon on your browser, then{' '}
        <strong><em>tap</em></strong> "Add to Home Screen".
      </p>
  </>
)}
</>
        ),
      },
      {
        title: "yesterday's solutions",
        content: (
          <>
            <YesterdaysSolutions />
          </>
        ),
      },
    ]

    const handleTitleClick = (index) => {
      setActiveIndex((curr) => index)
    }

    return (
      <div className="callout">
        <div className="accordion-titles">
          {items.map((item, index) => (
            <span
              key={index}
              className={`accordion-title ${index === 0 ? 'rules' : 'solutions'} ${index === activeIndex ? 'active' : ''}`}
              onClick={() => handleTitleClick(index)}
            >
              {item.title}
            </span>
          ))}
        </div>

        <div className="accordion-content">
          {items.map((item, index) => (
            <div
              key={index}
              className={`content ${index === activeIndex ? 'open' : ''}`}
            >
              {index === activeIndex && item.content}
            </div>
          ))}
        </div>
      </div>
    )
  }

  return (
    <div
      ref={gameContainerRef}
      tabIndex={0}
      onKeyDown={handleKeyDown}
      style={{
        outline: 'none',
        transition: 'background-color 1s ease-in-out',
        backgroundColor: winEffect
          ? isGameStarted
            ? `var(--target-color)`
            : `var(--uchu-yang)`
          : `var(--uchu-yang)`,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'flex-start',
        minHeight: '100vh',
      }}
    >
      {!loading && (
        <>
          <h1
            style={{
              transition: 'all 1s ease-in-out',
              fontFamily: 'Title',
              fontSize: '2.5em',
              fontWeight: 'normal',
              color: winEffect ? `var(--uchu-yin)` : `var(--uchu-yin)`,
              margin: '0.25em 0 0.5em',
            }}
          >
            <span
              style={{
                transition: 'all 1s ease-in-out',
                color: winEffect
                  ? isGameStarted
                    ? `var(--target-dark-color)`
                    : `var(--uchu-dark-gray)`
                  : 'var(--uchu-dark-gray)',
              }}
            >
              sub
            </span>
            letters
          </h1>
          <div
            style={{
              animation: shakeEffect
                ? 'var(--shake-animation)'
                : danceEffect
                  ? 'var(--dance-animation)'
                  : superDanceEffect
                    ? 'var(--super-dance-animation)'
                    : 'none',
              pointerEvents: isGameStarted ? 'auto' : 'auto',
              position: 'relative',
              margin: '0em auto 1em',
              maxWidth: '25em',
              width: '90%',
            }}
          >
            {/* card */}
            <div
              style={{
                backgroundColor: shakeEffect
                  ? 'var(--uchu-light-red)'
                  : isSolved(currentLevel).solved_optimally
                    ? `var(--solved-card-color)`
                    : `var(--level-${currentLevel}-color)`,
                borderRadius:
                  currentLevel === 1
                    ? '1em 1em 1em 0'
                    : currentLevel === 4
                      ? `1em 1em 0 1em`
                      : `1em 1em 1em 1em`,
                borderBottom: shakeEffect
                  ? `4px solid var(--uchu-red)`
                  : isSolved(currentLevel).solved_optimally
                    ? `4px solid var(--solved-card-border-color)`
                    : `4px solid var(--level-${currentLevel}-accent-color)`,
                borderLeft: shakeEffect
                  ? `4px solid var(--uchu-red)`
                  : isSolved(currentLevel).solved_optimally
                    ? `4px solid var(--solved-card-border-color)`
                    : `4px solid var(--level-${currentLevel}-accent-color)`,
                padding: '0.5em 2em',
                position: 'relative',
                zIndex: '1',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  position: 'relative',
                  width: `auto`,
                  maxWidth: '100%',
                  height: `64px`,
                  color: `var(--level-${currentLevel}-text-color)`,
                }}
              >
                <div
                  style={{
                    paddingRight: '0.5em',
                    fontSize: '1.5em',
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {wordPairs[currentLevel - 1][wordPairIndex][0]}
                </div>
                {/* sourced and modified from https://github.com/theproductfactory/handy-arrows */}
                {/* license: https://creativecommons.org/licenses/by/4.0/ */}
                <svg
                  width="100%"
                  height="100%"
                  viewBox="-2 -2 211 68"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M184.005 0.213186C182.621 0.77654 181.659 2.20837 181.659 3.71065C181.659 5.58849 182.409 6.41007 186.211 8.66349C188.557 10.0484 192.945 13.3111 193.109 13.8041C193.25 14.2031 190.622 14.3205 187.619 14.0388C183.559 13.6867 181.377 13.2877 177.388 12.161C171.006 10.4005 165.82 9.57892 161.009 9.57892C152.539 9.57892 146.367 12.4661 142.472 18.2639C139.563 22.6299 137.005 25.3528 135.503 25.7519C134.236 26.104 131.632 25.7519 126.516 24.5782C115.23 21.9727 110.936 21.5032 106.055 22.3717C98.3114 23.7097 92.6095 27.5123 85.8282 35.8218C80.4547 42.4177 78.9295 43.1219 70.6699 42.8402C63.0673 42.582 57.5061 41.8779 47.4397 39.9765C40.2594 38.5916 35.9184 38.3569 31.6948 39.0611C26.9079 39.8592 20.6663 42.4177 15.7856 45.5631C9.70821 49.5066 3.32578 55.7035 0.650786 60.3277C-0.405132 62.1351 -0.147018 63.567 1.21394 63.567C1.7067 63.567 2.62183 62.7923 5.81305 59.6235C11.6793 53.8022 15.6917 50.7742 21.3468 47.9104C25.6408 45.7274 29.841 44.5069 34.0882 44.1782C36.6458 43.9905 39.6493 44.4364 47.0642 46.103C57.9285 48.5208 64.2874 49.3423 72.899 49.3423C78.8591 49.3658 79.9385 49.2015 82.7308 47.7931C85.7578 46.2673 87.6349 44.5773 90.7558 40.6338C91.7413 39.3662 93.7358 37.1598 95.1672 35.7279C98.1003 32.7938 100.588 31.1272 103.615 30.0474C108.378 28.3104 112.32 28.4982 122.339 30.916C131.89 33.2163 134.588 33.5214 137.568 32.5825C140.994 31.5262 142.895 29.6484 147.611 22.6534C150.849 17.8884 153.243 16.7382 159.836 16.7617C163.027 16.7852 163.919 16.8791 167.11 17.5363C169.105 17.9588 172.578 18.8273 174.807 19.4611C177.036 20.0949 179.781 20.7756 180.908 20.9634C183.864 21.4563 189.918 21.8319 192.077 21.6441C193.837 21.4798 193.931 21.5033 193.743 21.9258C193.626 22.1605 193.274 23.0994 192.969 24.0148C191.842 27.1602 192.265 34.6716 193.626 35.3993C194.189 35.7044 194.376 35.4697 195.573 33.052C196.324 31.5028 198.06 28.9442 201.275 24.6017C203.809 21.1746 206.085 17.9588 206.367 17.4424C206.672 16.8321 206.86 16.034 206.86 15.2125C206.883 12.8182 205.71 11.4098 203.012 10.6118C201.909 10.2831 201.486 9.95452 199.539 7.91236C194.752 2.86565 186.985 -0.960467 184.005 0.213186Z"
                    fill={`var(--level-${currentLevel}-text-color)`}
                    transform="rotate(10 0 0) translate(12.5 -13) scale(0.9 0.75)"
                  />
                </svg>
                <div
                  style={{
                    paddingLeft: '0.5em',
                    fontSize: '1.5em',
                    fontWeight: 'bold',
                  }}
                >
                  {wordPairs[currentLevel - 1][wordPairIndex].slice(-1)[0]}
                </div>
              </div>
              <div style={{ position: 'relative' }}>
                <div className="card-text">
                  {currentWord.split('').map((char, index) => (
                    <span
                      key={index}
                      style={{
                        pointerEvents: isGameStarted ? 'auto' : 'none',
                        position: 'relative',
                        display: 'inline-block',
                        width: '1.25em',
                        color: `var(--level-${currentLevel}-text-color)`,
                        cursor: 'pointer',
                      }}
                      onClick={() => handleCharacterClick(index)}
                    >
                      {char}
                      {!isGameFinished && index === cursorPosition && (
                        <>
                          {/* Blinking cursor */}
                          <span
                            style={{
                              position: 'absolute',
                              bottom: '-0.125em',
                              left: '0',
                              width: '100%',
                              height: '4px',
                              backgroundColor: `var(--level-${currentLevel}-text-color)`,
                              borderRadius: '1em',
                              animation: 'blink 1s infinite',
                            }}
                          ></span>
                          {/* Tap animation */}
                          {!isGameStarted && isMobile && (
                            <span
                              className="tap"
                              style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '0.25em',
                                height: '0.25em',
                                borderRadius: '50%',
                                visibility: 'hidden',
                                backgroundColor: `var(--uchu-yin)`,
                                animation: 'tap 1.5s ease-out',
                              }}
                            ></span>
                          )}
                        </>
                      )}
                    </span>
                  ))}
                </div>
              </div>
              <ScoreCircles
                score={score}
                best={best}
                words={modalWords}
                currentLevel={currentLevel}
                handleWordClick={handleWordClick}
                expandedCircle={expandedCircle}
                handleScoreCircleClick={handleScoreCircleClick}
                handleBestCircleClick={handleBestCircleClick}
              />
            </div>
            {/* tab container */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-evenly', // Ensures equal spacing
                alignItems: 'center',
                height: '4em',
                width: 'calc(100%)', // Make the tab container take full width of the card
                position: 'absolute',
                bottom: '-3.5em',
                borderRadius: '0 0 1em 1em',
                margin: '0em auto 1em',
                gap: `0.25em`,
              }}
            >
              {/* tabs */}
              {[...Array(4)].map((_, i) => {
                const levelNum = i + 1

                const isNewlyAvailable =
                  levelNum !== 1 &&
                  isGameStarted &&
                  isSolved(levelNum - 1).solved &&
                  !hasUnlockedLevel(levelNum) &&
                  highestLevel < 4

                const isUnlocked = isGameStarted
                  ? levelNum <= highestLevel || hasUnlockedLevel(levelNum)
                  : levelNum === 1 || isSolved(levelNum - 1).solved

                const isVisible = isUnlocked

                const isSelected = levelNum === currentLevel
                return (
                  <div
                    key={i}
                    ref={isNewlyAvailable ? levelRef : null}
                    onClick={() => {
                      if (isNewlyAvailable) {
                        handleUnlockLevel(levelNum)
                      } else if (isUnlocked) {
                        changeLevel(levelNum)
                      }
                    }}
                    style={{
                      flex: '1',
                      maxWidth: '25%',
                      height: isSelected ? '1.5em' : '2.5em',
                      transform: isSelected
                        ? 'translate(0em, 1.8px)'
                        : 'translate(0em, -0.75em)',
                      borderRadius:
                        levelNum === 4 && !isSelected
                          ? '0.5em 0.5em 0.5em 0.5em'
                          : '0 0 0.5em 0.5em',
                      borderBottom: isSolved(levelNum).solved_optimally
                        ? isSelected
                          ? shakeEffect
                            ? `4px solid var(--uchu-red)`
                            : `4px solid var(--solved-card-border-color)`
                          : `4px solid var(--solved-card-border-dark-color)`
                        : isSelected
                          ? shakeEffect
                            ? `4px solid var(--uchu-red)`
                            : `4px solid var(--level-${levelNum}-accent-color)`
                          : isNewlyAvailable
                            ? `4px solid var(--uchu-gray-5)`
                            : `4px solid var(--uchu-gray-5)`,
                      borderLeft: isSolved(levelNum).solved_optimally
                        ? isSelected
                          ? shakeEffect
                            ? `4px solid var(--uchu-red)`
                            : `4px solid var(--solved-card-border-color)`
                          : `4px solid var(--solved-card-border-dark-color)`
                        : isSelected
                          ? shakeEffect
                            ? `4px solid var(--uchu-red)`
                            : `4px solid var(--level-${levelNum}-accent-color)`
                          : isNewlyAvailable
                            ? `4px solid var(--uchu-gray-5)`
                            : `4px solid var(--uchu-gray-5)`,
                      cursor:
                        isNewlyAvailable || isVisible ? 'pointer' : 'default',

                      visibility: isVisible ? 'visible' : 'hidden',

                      animation: isNewlyAvailable
                        ? 'slide-down 1s ease forwards'
                        : 'none',
                      animationDelay: isNewlyAvailable
                        ? danceEffect
                          ? '1.5s, 2s'
                          : superDanceEffect
                            ? '3s, 3.5s'
                            : '0s, 0s'
                        : '0s, 0s',
                      justifyContent: 'center',
                      alignItems: 'center',
                      display: 'flex',
                      fontSize: '1em',
                      fontWeight: 'bold',
                      backgroundColor: isVisible
                        ? isSelected
                          ? shakeEffect
                            ? `var(--uchu-light-red)`
                            : isSolved(levelNum).solved_optimally
                              ? `var(--solved-card-color)`
                              : `var(--level-${levelNum}-color)`
                          : isSolved(levelNum).solved_optimally
                            ? `var(--solved-card-dark-color)`
                            : `var(--uchu-gray-2)`
                        : isNewlyAvailable
                          ? `var(--uchu-gray-2)`
                          : 'transparent',
                      zIndex: isSelected ? '2' : '0',
                      WebkitTapHighlightColor: 'transparent',
                      outline: 'none',
                      touchAction: 'manipulation',
                    }}
                  ></div>
                )
              })}
            </div>
          </div>

            <div
              className={`completion-message ${
                showCompletionMessage ? 'show' : 'hide'
              }`}
            >
              {completionMessage.split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  <br />
                </span>
              ))}
            </div>

          {isGameStarted && (
            <QwertyKeyboard
              handleKeyDown={handleKeyDown}
              handleShare={handleShare}
              handleHelpClick={handleHelpClick}
            />
          )}
          {viewInstructions && <Accordion />}
          <div
            style={{
              display: 'flex',
              marginBottom: 'auto',
              alignItems: 'center',
              position: isGameStarted ? 'inherit' : 'relative',
              marginTop: isGameStarted ? '0' : '-1em',
            }}
          ></div>
          {isGameStarted && message && (
            <Alert message={message} setMessage={setMessage}>
              <span>{message}</span>
            </Alert>
          )}
        </>
      )}
      <footer
        style={{
          transition: 'all 1s ease-in-out',
          backgroundColor: winEffect
            ? isGameStarted
              ? `var(--target-color)`
              : `var(--uchu-yang)`
            : 'var(--uchu-yang)',
          color: winEffect
            ? isGameStarted
              ? `var(--target-dark-color)`
              : `var(--uchu-gray)`
            : 'var(--uchu-gray)',
        }}
      >
        <p style={{}}>
          <span style={{ fontFamily: 'Title', fontSize: '1.4em' }}>
            subletters
          </span>
          <sup style={{ fontSize: '0.8em' }}>™</sup>
        </p>
        <p style={{}}>&copy; 2025 Alexander Bolinsky and Peter Branden</p>
      </footer>
    </div>
  )
}

export default Game
